import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Spin, Alert, Empty } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { SiteType } from '../../../graphql/types/SiteType';
import { useCheckWarehouseGrayConfig } from '../../../graphql/queries/checkWarehouseGrayConfig';
import { WORKBENCH_METRICS_BAR } from './graphql/queries/workbenchMetricsBar';
import { WORKBENCH_BOARD } from './graphql/queries/workbenchBoard';
import { WORKBENCH_SHIFT_REPORT } from './graphql/queries/workbenchShiftReport';
import ShiftBanner from './components/ShiftBanner';
import { composeDeadlineLabel } from './utils/taskTextComposer';
import MetricsBar from './components/MetricsBar';
import TasksSection from './components/TasksSection';
import AlertsSection from './components/AlertsSection';
import PeriodReviewSection from './components/PeriodReviewSection';
import ShiftReportSection from './components/ShiftReportSection';

dayjs.extend(utc);
dayjs.extend(timezone);

// 实际 5 分钟拉一次。页面文案（实时告警栏的「每 30 分钟刷新」等）是给仓管的心理预期下界，
// 拉得更勤只会更好，故文案与真实周期刻意不联动
const POLL_INTERVAL = 5 * 60 * 1000;

/**
 * 跨零点后仍属前一班次，日报要继续可见到仓本地 04:00。上游 `editable` 含「当日报告」条件，
 * 一过零点即置假，日报块会在班次没结束时消失。
 */
const SHIFT_ROLLOVER_HOUR = 4;

/**
 * 日报整块忽略编辑窗口一直展示的按仓灰度开关。未配置时回落到上游窗口判定：
 * `canEdit` 为真表示在仓本地编辑窗口内（默认 18:00 后，可按仓配置），
 * 已发送后上游会置 canEdit=false 但仍要能看，故与 SENT 取或。
 */
const ALWAYS_SHOW_DAILY_REPORT_CONFIG_KEY = 'wms.workbench.daily_report.always_show';

const MyWorkbenchPage: React.FC = () => {
  const { t } = useTranslation();
  const { currentWarehouse } = useWarehouseContext();
  const [isVisible, setIsVisible] = useState(true);
  // 本页是 WHS 维度，选中 HUB 时不发请求（口径同 WmsLocationManagementPage）：
  // 选中项跨页面共用一个 localStorage key，在片区复盘选了 HUB 再回本页刷新会带 HUB id 进来，
  // 当成 warehouseId 发出去后端会报错
  const warehouseId = useMemo(() => {
    if (!currentWarehouse?.id) return undefined;
    return Number(currentWarehouse.siteType) === SiteType.WAREHOUSE
      ? Number(currentWarehouse.id)
      : undefined;
  }, [currentWarehouse]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(document.visibilityState === 'visible');
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // KPI 条与任务面板已接真实接口，各块 skip 独立判定
  const {
    data: metricsRaw,
    loading: metricsLoading,
    error: metricsError,
    refetch: refetchMetrics,
    startPolling: startPollingMetrics,
    stopPolling: stopPollingMetrics,
  } = useQuery(WORKBENCH_METRICS_BAR, {
    variables: { warehouseId, statDate: null },
    skip: !warehouseId,
    pollInterval: POLL_INTERVAL,
  });

  // 任务 / 实时告警 / 周期复盘三栏一次拉全：上游 board 与页面三栏 1:1，
  // 分三次拉既多两次 RPC，又拿不到仓时区
  const {
    data: boardRaw,
    loading: boardLoading,
    error: boardError,
    refetch: refetchBoard,
    startPolling: startPollingBoard,
    stopPolling: stopPollingBoard,
  } = useQuery(WORKBENCH_BOARD, {
    variables: { warehouseId },
    skip: !warehouseId,
    pollInterval: POLL_INTERVAL,
  });

  // 不再等 board 的 bizDate：上游已把业务日从入参里删掉，日报与 board 各自取当前业务日，
  // 两条查询可并行发起
  const {
    data: reportRaw,
    error: reportError,
    refetch: refetchReport,
    startPolling: startPollingReport,
    stopPolling: stopPollingReport,
  } = useQuery(WORKBENCH_SHIFT_REPORT, {
    variables: { warehouseId },
    skip: !warehouseId,
    // 日报里的风险条目会被别人（片区经理、其他班次）处理，不轮询就只能靠自己操作后才刷新
    pollInterval: POLL_INTERVAL,
  });

  const { isInGrayConfig: alwaysShowDailyReport } = useCheckWarehouseGrayConfig(
    ALWAYS_SHOW_DAILY_REPORT_CONFIG_KEY,
    currentWarehouse?.id,
    !currentWarehouse,
  );

  // KPI 条与 board 是两条独立查询，轮询也各自控制
  useEffect(() => {
    if (!warehouseId) return;
    if (!isVisible) {
      stopPollingMetrics();
    } else {
      refetchMetrics();
      startPollingMetrics(POLL_INTERVAL);
    }
  }, [isVisible, warehouseId, stopPollingMetrics, refetchMetrics, startPollingMetrics]);

  // 页面切到后台就停轮询，避免无人看时白拉数据；回到前台先补一次再恢复轮询
  useEffect(() => {
    if (!warehouseId) return;
    if (!isVisible) {
      stopPollingBoard();
    } else {
      refetchBoard();
      startPollingBoard(POLL_INTERVAL);
    }
  }, [isVisible, warehouseId, stopPollingBoard, refetchBoard, startPollingBoard]);

  useEffect(() => {
    if (!warehouseId) return;
    if (!isVisible) {
      stopPollingReport();
    } else {
      refetchReport();
      startPollingReport(POLL_INTERVAL);
    }
  }, [isVisible, warehouseId, stopPollingReport, refetchReport, startPollingReport]);

  const handleRefresh = useCallback(() => {
    refetchMetrics();
    refetchBoard();
    refetchReport();
  }, [refetchMetrics, refetchBoard, refetchReport]);

  const metricsBar = metricsRaw?.workbenchMetricsBar;
  const board = boardRaw?.workbenchBoard;
  const tasks = board?.tasks;
  const alerts = board?.liveAlerts;
  const period = board?.periodAlerts;
  // 栏位开关来自上游 Nacos，与 alerts / period 是否为空是两件事：
  // 为空是「这次没数据」，仍占位；开关为假才是「该仓不该有这一栏」，连栅格轨道一起撤掉
  const showAlertsColumn = !!board?.showLiveAlerts;
  const showPeriodColumn = !!board?.showPeriodAlerts;
  // 仓时区取自上游；为空表示上游未返回或无法解析，下游一律降级为 —，不回落浏览器时钟
  const timeZone = board?.timeZone ?? null;
  const report = reportRaw?.workbenchShiftReport;
  // 时区缺省时不猜：宁可按上游窗口判定，也不拿浏览器时钟当仓本地时间
  const localHour = board?.serverNow && timeZone
    ? dayjs(board.serverNow).tz(timeZone).hour()
    : null;
  const inShiftTail = localHour !== null && localHour < SHIFT_ROLLOVER_HOUR;
  const showReport = !!report?.available
    && (alwaysShowDailyReport || report.canEdit || inShiftTail || report.status === 'SENT');

  const hasError = metricsError || boardError || reportError;
  const isLoading = boardLoading;

  // 横幅是「X 截止前还有 N 项待办」，N 覆盖全部未完成任务，故 X 取这批任务里**最晚**的截止时刻 ——
  // 取最早的会说成「04:00 截止前还有 11 项」，而其中一项实际 06:00 才截止。
  // 文案由前端拼（后端不再下发 deadlineLabel）
  const lastDeadline = (tasks?.tasks ?? [])
    .map((task: { windowEndAt?: string | null }) => task.windowEndAt)
    .filter((endAt: string | null | undefined): endAt is string => !!endAt)
    .sort()
    .pop();
  const pendingCount = tasks ? (tasks.totalCount ?? 0) - (tasks.doneCount ?? 0) : 0;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  // 选中的是 HUB：上面已把 warehouseId 置空、请求全部 skip，不兜底会渲染成一片空白
  if (!warehouseId) {
    return <Empty description={String(t('common.noData'))} />;
  }

  return (
    // 页面高度不锁死（口径同片区复盘页）：整页由 MainLayout 的滚动容器负责，
    // 三栏的内滚由各自内容的 max-h 兜住。若在此处 h-full 分配剩余高度，
    // 顶部横幅 / KPI / 日报一变高就会把三栏压到只剩标题，且整页无从滚动
    <div className="bg-white rounded-lg shadow-sm">
      {hasError && (
        <Alert
          message={String(t('common.error'))}
          description={String(t('common.errorOccurred'))}
          type="error"
          showIcon
          closable
        />
      )}

      {/* 1 顶部横幅：抵消 WmsLayout 的 p-6，浅紫渐变贴到内容区上/左/右边缘 */}
      {board && tasks && (
        <div className="-mx-6 -mt-6">
          <ShiftBanner
            data={{
              // 仓名取本地仓库选择器的当前仓，与页面其余部分同源
              warehouseName: currentWarehouse?.name ?? '',
              timeZone,
              serverNow: board.serverNow,
              // 横幅是全 4 类任务口径，由后端取上游汇总直出；不能用任务栏的 tasks.doneCount
              // 兜底，那份只含 DO 两类，混用会把少算的数字显示成对的
              todayTasks: board.todayTasks,
              // 仓库排名口径未定义、上游无提供方（plan W4），暂不渲染排名卡
              ranking: null,
              deadlineLabel: composeDeadlineLabel(lastDeadline, timeZone),
              pendingCount,
            }}
          />
        </div>
      )}

      {/* 2 KPI 条：真实数据 */}
      <MetricsBar
        metrics={metricsBar?.metrics ?? []}
        calculatedAt={metricsBar?.calculatedAt}
        timeZone={timeZone}
        noUpdateThresholdHours={metricsBar?.noUpdateThresholdHours}
        loading={metricsLoading}
      />

      <div className="py-6 space-y-6">
        {/* 3 日报：上游未生成或不在编辑窗口内时整块不渲染 */}
        {showReport && (
          <ShiftReportSection
            data={report}
            warehouseId={warehouseId}
            timeZone={timeZone}
            onDraftSaved={handleRefresh}
            /* 「已处理风险」按当前显示的告警栏合计：Nacos 关掉的栏（showLiveAlerts / showPeriodAlerts 为 false）
               不进分子分母；两栏都关时传 null，日报卡内整格不渲染。
               上游 kpi.riskTotal 是硬合并，无视开关，会让仓管对不上页面上真正显示的两栏 */
            alertsCountOverride={(() => {
              if (!showAlertsColumn && !showPeriodColumn) {
                return null;
              }
              const liveHandled = showAlertsColumn ? (alerts?.handledCount ?? 0) : 0;
              const liveUnhandled = showAlertsColumn ? (alerts?.activeCount ?? 0) : 0;
              const perHandled = showPeriodColumn ? (period?.handledCount ?? 0) : 0;
              const perUnhandled = showPeriodColumn ? (period?.activeCount ?? 0) : 0;
              const handled = liveHandled + perHandled;
              const unhandled = liveUnhandled + perUnhandled;
              return { handled, total: handled + unhandled, unhandled };
            })()}
          />
        )}

        {/* 4 三列并排 */}
        {/* 比例仍是 5:3:3（告警与周期复盘同为告警列表，宽度须一致）。
            不用 grid-cols-11：Tailwind 会展开成 repeat(11, minmax(0,1fr))，
            minmax 的 0 会吃掉轨道自动最小值，长司机名撑不开列。改写显式轨道，
            后两列以 min-content 兜底，超出内容区时由 MainLayout 承接页面级横滚。
            任务栏的 20rem 下限不能省：写成 minmax(0,5fr) 的话长司机名撑宽告警列的代价
            会全部由任务栏吞掉（可压缩到 0），页面反而不滚。
            不设窄屏单列断点：三列的横向对照是这个页面的用法本身，窄屏改为横滚而非堆叠。
            轨道按开关动态拼（不能写成 Tailwind 动态类名，那样会被 JIT 漏扫）：
            关掉的栏必须连轨道一起去掉，只藏 div 会在右侧留一条空白 */}
        <div
          className="grid gap-x-2 gap-y-6 items-start"
          style={{
            gridTemplateColumns: [
              'minmax(20rem,5fr)',
              showAlertsColumn ? 'minmax(min-content,3fr)' : null,
              showPeriodColumn ? 'minmax(min-content,3fr)' : null,
            ]
              .filter(Boolean)
              .join(' '),
          }}
        >
          <div>
            {tasks && (
              <TasksSection
                data={tasks}
                timeZone={timeZone}
                warehouseId={warehouseId}
                onTaskComplete={handleRefresh}
              />
            )}
          </div>
          {showAlertsColumn && (
            <div>
              {alerts && (
                <AlertsSection
                  data={alerts}
                  warehouseId={warehouseId}
                  timeZone={timeZone}
                  onHandled={handleRefresh}
                />
              )}
            </div>
          )}
          {showPeriodColumn && (
            <div>
              {period && (
                <PeriodReviewSection
                  data={period}
                  warehouseId={warehouseId}
                  timeZone={timeZone}
                  onCommentSaved={handleRefresh}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyWorkbenchPage;
